import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
    Variable,
    createVariable,
    updateVariable,
} from '../../../services/api/departmentVariablesService';
import { VariableDefault } from '../utils/variableTypes';
import { toLabel, toName } from '../utils/variableUtils';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';
import { X, Plus } from 'lucide-react';
import RefVariableField from './WizardRefField';
import FixedVariableField from './WizardFixedField';

export interface DepartmentUser {
    id: string;
    name: string;
    role: string;
}

export interface VariableFormHandle {
    submit: () => Promise<boolean>;
    isDirty: boolean;
    openAdd: () => void;
}

type AllowCustom =
    | { type: 'fixed' }
    | { type: 'ref'; refTarget: string }
    | false;

interface VariableFormProps {
    defaults: VariableDefault[];
    saved: Variable[];
    users: DepartmentUser[];
    onSaved: () => void | Promise<void>;
    onValidityChange?: (isValid: boolean) => void;
    onSubmitValues?: (values: Record<string, Variable>) => Promise<void> | void;
    allowCustom?: AllowCustom;
    hideFooter?: boolean;
    hideAddButton?: boolean;
    onDeleteSaved?: (name: string) => Promise<boolean> | boolean;
}

const buildInitialValues = (
    defaults: VariableDefault[],
    saved: Variable[]
): Record<string, Variable> => {
    const map: Record<string, Variable> = {};
    for (const def of defaults) {
        const existing = saved.find((s) => s.name === def.name);
        const type = def.field === 'ref' ? 'ref' : 'fixed';
        map[def.name] = existing
            ? { ...existing }
            : {
                  type,
                  name: def.name,
                  ...(def.field === 'ref' && { refTarget: def.refTarget }),
              };
    }
    return map;
};

export default forwardRef<VariableFormHandle, VariableFormProps>(
    function WizardVariableForm(
        {
            defaults,
            saved,
            users,
            onSaved,
            onValidityChange,
            onSubmitValues,
            allowCustom = false,
            hideFooter = false,
            hideAddButton = false,
            onDeleteSaved,
        },
        ref
    ) {
        const { toast } = useToast();
        const { t } = useTranslation();

        const [values, setValues] = useState<Record<string, Variable>>(() =>
            buildInitialValues(defaults, saved)
        );
        const [customDefs, setCustomDefs] = useState<VariableDefault[]>([]);
        const [customInput, setCustomInput] = useState('');
        const [showCustomInput, setShowCustomInput] = useState(false);
        const [saving, setSaving] = useState(false);
        const [requiredErrors, setRequiredErrors] = useState<string[]>([]);

        const initialValuesJson = useRef(
            JSON.stringify(buildInitialValues(defaults, saved))
        );

        const allDefs = useMemo(
            () => [...defaults, ...customDefs],
            [defaults, customDefs]
        );

        const isDirty = useMemo(
            () => JSON.stringify(values) !== initialValuesJson.current,
            [values]
        );

        const getMissingRequiredNames = (
            currentValues: Record<string, Variable>
        ): string[] =>
            defaults
                .filter((def) => def.required)
                .filter((def) => {
                    const current = currentValues[def.name];
                    if (!current) return true;

                    if (def.field === 'ref') {
                        return !current.refId && !current.fixedValue?.trim();
                    }

                    return !current.fixedValue?.trim();
                })
                .map((def) => def.name);

        useEffect(() => {
            const isValid = getMissingRequiredNames(values).length === 0;
            onValidityChange?.(isValid);
        }, [values, defaults, onValidityChange]);

        const updateFixed = (name: string, fixedValue: string) =>
            setValues((prev) => ({
                ...prev,
                [name]: { ...prev[name], fixedValue },
            }));

        const selectUser = (name: string, refId: string) =>
            setValues((prev) => ({
                ...prev,
                [name]: { ...prev[name], refId, fixedValue: undefined },
            }));

        const clearContact = (name: string) =>
            setValues((prev) => ({
                ...prev,
                [name]: {
                    ...prev[name],
                    refId: undefined,
                    fixedValue: undefined,
                },
            }));

        const clearRequiredErrorFor = (name: string) => {
            setRequiredErrors((prev) => prev.filter((key) => key !== name));
        };

        const removeField = async (name: string) => {
            if (onDeleteSaved && saved.some((s) => s.name === name)) {
                const removed = await onDeleteSaved(name);
                if (removed === false) {
                    return;
                }
            }
            setCustomDefs((prev) => prev.filter((d) => d.name !== name));
            setValues((prev) => {
                const next = { ...prev };
                delete next[name];
                return next;
            });
        };
        const onSubmit = async (): Promise<boolean> => {
            const missingRequired = getMissingRequiredNames(values);
            if (missingRequired.length > 0) {
                setRequiredErrors(missingRequired);
                const firstMissing = defaults.find(
                    (def) => def.name === missingRequired[0]
                );
                toast({
                    title: t('departmentVariables.wizardForm.requiredFieldMissing', {
                        field: firstMissing?.label || toLabel(missingRequired[0]),
                    }),
                    variant: 'destructive',
                });
                return false;
            }

            setSaving(true);
            try {
                if (onSubmitValues) {
                    await onSubmitValues(values);
                    initialValuesJson.current = JSON.stringify(values);
                    setCustomDefs([]);
                    setRequiredErrors([]);
                    await onSaved();
                    return true;
                }

                const initial = JSON.parse(initialValuesJson.current) as Record<
                    string,
                    Variable
                >;
                const changed = Object.keys(values).filter(
                    (name) =>
                        JSON.stringify(values[name]) !==
                        JSON.stringify(initial[name])
                );
                if (changed.length === 0) {
                    setRequiredErrors([]);
                    return true;
                }
                await Promise.all(
                    changed.map((name) => {
                        const { type, fixedValue, refTarget, refId } =
                            values[name];
                        return saved.some((v) => v.name === name)
                            ? updateVariable(name, {
                                  type,
                                  fixedValue,
                                  refTarget,
                                  refId,
                              })
                            : createVariable({
                                  type,
                                  name,
                                  fixedValue,
                                  refTarget,
                                  refId,
                              });
                    })
                );
                toast({ title: t('departmentVariables.wizardForm.settingsSaved') });
                initialValuesJson.current = JSON.stringify(values);
                setCustomDefs([]);
                setRequiredErrors([]);
                await onSaved();
                return true;
            } catch {
                toast({
                    title: t('departmentVariables.wizardForm.failedToSave'),
                    variant: 'destructive',
                });
                return false;
            } finally {
                setSaving(false);
            }
        };

        const onSubmitRef = useRef(onSubmit);
        onSubmitRef.current = onSubmit;

        useImperativeHandle(
            ref,
            () => ({
                submit: () => onSubmitRef.current(),
                isDirty,
                openAdd: () => setShowCustomInput(true),
            }),
            [isDirty]
        );

        const handleCustomSubmit = () => {
            const label = customInput.trim();
            if (!label) return;
            const name = toName(label);

            if (
                allDefs.some((d) => d.name === name) ||
                saved.some((v) => v.name === name)
            ) {
                toast({
                    title: t('departmentVariables.wizardForm.variableExists'),
                    variant: 'destructive',
                });
                return;
            }

            const refTarget =
                allowCustom && allowCustom.type === 'ref'
                    ? allowCustom.refTarget
                    : undefined;
            const newDef: VariableDefault = refTarget
                ? { field: 'ref', name, label, refTarget }
                : { field: 'text', name, label };

            setCustomDefs((prev) => [...prev, newDef]);
            setValues((prev) => ({
                ...prev,
                [name]: refTarget
                    ? { type: 'ref', name, refTarget }
                    : { type: 'fixed', name },
            }));
            setCustomInput('');
            setShowCustomInput(false);
        };

        const renderField = (def: VariableDefault) => {
            const current = values[def.name];
            if (!current) return null;
            const isRemovable =
                !!def.removable || customDefs.some((d) => d.name === def.name);

            if (def.field === 'ref') {
                return (
                    <RefVariableField
                        key={def.name}
                        def={def}
                        current={current}
                        users={users}
                        isRemovable={isRemovable}
                        onSelectUser={(refId) => {
                            clearRequiredErrorFor(def.name);
                            selectUser(def.name, refId);
                        }}
                        onClearContact={() => {
                            clearContact(def.name);
                        }}
                        onRemove={() => {
                            void removeField(def.name);
                        }}
                        onSwitchToExternal={() => {}}
                        onFixedChange={(value) => {
                            clearRequiredErrorFor(def.name);
                            updateFixed(def.name, value);
                        }}
                    />
                );
            }

            return (
                <FixedVariableField
                    key={def.name}
                    def={def}
                    value={current.fixedValue || ''}
                    isRemovable={isRemovable}
                    onRemove={() => {
                        void removeField(def.name);
                    }}
                    onChange={(value) => {
                        clearRequiredErrorFor(def.name);
                        updateFixed(def.name, value);
                    }}
                />
            );
        };

        const addLabel =
            allowCustom && allowCustom.type === 'ref'
                ? t('departmentVariables.wizardForm.addContact')
                : t('departmentVariables.wizardForm.addVariable');
        const customPlaceholder =
            allowCustom && allowCustom.type === 'ref'
                ? t('departmentVariables.wizardForm.contactPlaceholder')
                : t('departmentVariables.wizardForm.variablePlaceholder');

        return (
            <div className="space-y-4">
                {requiredErrors.length > 0 && (
                    <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                        {t('departmentVariables.wizardForm.requiredFieldsError')}
                    </div>
                )}
                {allDefs.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {allDefs.map(renderField)}
                    </div>
                )}

                {allowCustom && !showCustomInput && !hideAddButton && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowCustomInput(true)}
                    >
                        <Plus className="h-4 w-4" />
                        {addLabel}
                    </Button>
                )}

                {showCustomInput && (
                    <div className="flex items-center gap-2">
                        <Input
                            placeholder={customPlaceholder}
                            value={customInput}
                            onChange={(e) => setCustomInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleCustomSubmit();
                                }
                            }}
                            autoFocus
                        />
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleCustomSubmit}
                            disabled={!customInput.trim()}
                        >
                            {t('departmentVariables.wizardForm.add')}
                        </Button>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                                setShowCustomInput(false);
                                setCustomInput('');
                            }}
                            className="hover:text-destructive"
                            aria-label="Cancel"
                        >
                            <X className="h-4 w-4" />
                        </Button>
                    </div>
                )}

                {!hideFooter &&
                    (allDefs.length > 0 || customDefs.length > 0) && (
                        <div className="flex justify-end pt-2">
                            <Button
                                type="button"
                                onClick={onSubmit}
                                disabled={
                                    saving ||
                                    requiredErrors.length > 0 ||
                                    !isDirty
                                }
                            >
                                {saving ? t('departmentVariables.wizardForm.saving') : t('departmentVariables.wizardForm.save')}
                            </Button>
                        </div>
                    )}
            </div>
        );
    }
);

